import { useState, useEffect, useRef, useContext } from "react";
import useTemplates from "../../../services/Whatsapp/useTemplates";
import useWhatsappNumbers from "../../../services/Whatsapp/useWhatsappNumbers";
import "../../whatsapp/WhatsApp.css";
import { GlobalContext } from "../../../Context/GlobalContext";
import useMedia from "../../../services/Whatsapp/useMedia";
import NoIndex from "../../../components/NoIndex";
import { toast, ToastContainer } from "react-toastify";

const CreateTemplate = () => {
  const { getLanguages } = useTemplates();
  const { whatsappNumbers } = useWhatsappNumbers();

  const {
    createTemplate,
    createImageTemplate,
    createVideoTemplate,
    createDocumentTemplate,
    fetchVendorTemplates,
  } = useTemplates();

  const { uploadImage, uploadVideo, uploadDocument } = useMedia();

  const { secret, userID } = useContext(GlobalContext);
  const [languages, setLanguages] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState("");
  const [includeHeader, setIncludeHeader] = useState(false);
  const [includeButtons, setIncludeButtons] = useState(false);
  const [headerImage, setHeaderImage] = useState(null);
  const [headerVideo, setHeaderVideo] = useState(null);
  const [headerDocument, setHeaderDocument] = useState(null);
  const textareaRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedEmojiCategory, setSelectedEmojiCategory] = useState("😀");
  const [selectedCode, setSelectedCode] = useState("91");
  const [variables, setVariables] = useState([]);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };

      reader.onerror = (error) => reject(error);
    });
  };

  const formatFileSize = (bytes = 0) => {
    if (!bytes) return "0 KB";

    const kb = bytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const getDocumentExtension = (fileName = "") => {
    const parts = fileName.split(".");
    return parts.length > 1 ? parts.pop().toUpperCase() : "DOC";
  };

  const getBodyVariableExamples = () => {
    const bodyText = template.body || "";

    // Supports {{1}}, {{ 1 }}, {{2}}, {{ 2 }}
    const matches = bodyText.match(/{{\s*\d+\s*}}/g) || [];

    if (matches.length === 0) {
      return [];
    }

    const variableNumbers = [
      ...new Set(
        matches
          .map((item) => item.match(/\d+/)?.[0])
          .filter(Boolean)
          .map(Number),
      ),
    ].sort((a, b) => a - b);

    return variableNumbers.map((num, index) => {
      return variables[num - 1]?.value?.trim() || `sample ${index + 1}`;
    });
  };

  const normalizeBodyVariablesForPayload = () => {
    const bodyText = template.body || "";

    const matches = bodyText.match(/{{\s*\d+\s*}}/g) || [];

    if (matches.length === 0) {
      return {
        text: bodyText,
        exampleValues: [],
      };
    }

    let counter = 0;
    const usedMap = {};

    const normalizedText = bodyText.replace(
      /{{\s*(\d+)\s*}}/g,
      (match, num) => {
        if (!usedMap[num]) {
          counter += 1;
          usedMap[num] = counter;
        }

        return `{{${usedMap[num]}}}`;
      },
    );

    const exampleValues = Object.keys(usedMap)
      .sort((a, b) => usedMap[a] - usedMap[b])
      .map((oldNum, index) => {
        return (
          variables[Number(oldNum) - 1]?.value?.trim() || `sample ${index + 1}`
        );
      });

    return {
      text: normalizedText,
      exampleValues,
    };
  };

  const buildMediaPayload = (mediaUrl, type) => {
    const components = [];

    // BODY
    if (template.body?.trim()) {
      const normalizedBody = normalizeBodyVariablesForPayload();

      const bodyComponent = {
        type: "BODY",
        text: normalizedBody.text,
      };

      if (normalizedBody.exampleValues.length > 0) {
        bodyComponent.example = {
          body_text: [normalizedBody.exampleValues],
        };
      }

      components.push(bodyComponent);
    }

    // FOOTER
    if (template.footer?.trim()) {
      components.push({
        type: "FOOTER",
        text: template.footer,
      });
    }

    // BUTTONS
    const buttons = [];

    if (includeButtons && template.showButton4_quick1?.trim()) {
      buttons.push({
        type: "QUICK_REPLY",
        text: template.showButton4_quick1,
      });
    }

    if (includeButtons && template.showButton4_quick2?.trim()) {
      buttons.push({
        type: "QUICK_REPLY",
        text: template.showButton4_quick2,
      });
    }

    if (
      includeButtons &&
      template.showButton4_quick3?.trim() &&
      template.showButton4_quick3_url?.trim()
    ) {
      buttons.push({
        type: "URL",
        text: template.showButton4_quick3,
        url: template.showButton4_quick3_url,
      });
    }

    if (
      includeButtons &&
      template.showButton4_quick4?.trim() &&
      template.showButton4_quick3_call?.trim()
    ) {
      buttons.push({
        type: "PHONE_NUMBER",
        text: template.showButton4_quick4,
        phone_number: template.showButton4_quick3_call,
      });
    }

    if (buttons.length > 0) {
      components.push({
        type: "BUTTONS",
        buttons,
      });
    }

    return {
      userid: userID,
      secret: secret || "string",
      name: template.name,
      language: template.language,
      category: template.category,
      purpose: "",
      phoneNumberId: selectedNumber,
      [type]: mediaUrl,
      components,
    };
  };

  const buildCreateTemplatePayload = () => {
    const components = [];

    // BODY
    if (template.body?.trim()) {
      const bodyComponent = {
        type: "BODY",
        text: template.body,
      };

      const variableValues = getBodyVariableExamples();

      if (variableValues.length > 0) {
        bodyComponent.example = {
          body_text: [variableValues],
        };
      }

      components.push(bodyComponent);
    }

    // HEADER TEXT
    if (
      includeHeader &&
      template.headerType === "Text" &&
      template.headerText?.trim()
    ) {
      components.push({
        type: "HEADER",
        format: "TEXT",
        text: template.headerText,
      });
    }

    // FOOTER
    if (template.footer?.trim()) {
      components.push({
        type: "FOOTER",
        text: template.footer,
      });
    }

    // BUTTONS
    const buttons = [];

    if (includeButtons && template.showButton4_quick1?.trim()) {
      buttons.push({
        type: "QUICK_REPLY",
        text: template.showButton4_quick1,
      });
    }

    if (includeButtons && template.showButton4_quick2?.trim()) {
      buttons.push({
        type: "QUICK_REPLY",
        text: template.showButton4_quick2,
      });
    }

    if (
      includeButtons &&
      template.showButton4_quick3?.trim() &&
      template.showButton4_quick3_url?.trim()
    ) {
      buttons.push({
        type: "URL",
        text: template.showButton4_quick3,
        url: template.showButton4_quick3_url,
      });
    }

    if (
      includeButtons &&
      template.showButton4_quick4?.trim() &&
      template.showButton4_quick3_call?.trim()
    ) {
      buttons.push({
        type: "PHONE_NUMBER",
        text: template.showButton4_quick4,
        phone_number: template.showButton4_quick3_call,
      });
    }

    if (buttons.length > 0) {
      components.push({
        type: "BUTTONS",
        buttons,
      });
    }

    return {
      userid: userID,
      secret: secret || "string",
      name: template.name,
      language: template.language,
      category: template.category,
      purpose: "",
      phoneNumberId: selectedNumber,
      components,
    };
  };

  const buildDocumentTemplatePayload = (documentUrl) => {
    const components = [];

    // BODY
    if (template.body?.trim()) {
      const normalizedBody = normalizeBodyVariablesForPayload();

      const bodyComponent = {
        type: "BODY",
        text: normalizedBody.text,
      };

      if (normalizedBody.exampleValues.length > 0) {
        bodyComponent.example = {
          body_text: [normalizedBody.exampleValues],
        };
      }

      components.push(bodyComponent);
    }

    // FOOTER
    if (template.footer?.trim()) {
      components.push({
        type: "FOOTER",
        text: template.footer,
      });
    }

    // BUTTONS
    const buttons = [];

    if (includeButtons && template.showButton4_quick1?.trim()) {
      buttons.push({
        type: "QUICK_REPLY",
        text: template.showButton4_quick1,
      });
    }

    if (includeButtons && template.showButton4_quick2?.trim()) {
      buttons.push({
        type: "QUICK_REPLY",
        text: template.showButton4_quick2,
      });
    }

    if (
      includeButtons &&
      template.showButton4_quick3?.trim() &&
      template.showButton4_quick3_url?.trim()
    ) {
      buttons.push({
        type: "URL",
        text: template.showButton4_quick3,
        url: template.showButton4_quick3_url,
      });
    }

    if (
      includeButtons &&
      template.showButton4_quick4?.trim() &&
      template.showButton4_quick3_call?.trim()
    ) {
      buttons.push({
        type: "PHONE_NUMBER",
        text: template.showButton4_quick4,
        phone_number: template.showButton4_quick3_call,
      });
    }

    if (buttons.length > 0) {
      components.push({
        type: "BUTTONS",
        buttons,
      });
    }

    return {
      userid: userID,
      secret: secret || "string",
      name: template.name,
      language: template.language,
      category: template.category,
      purpose: "",
      phoneNumberId: selectedNumber,
      document: documentUrl,

      components,
    };
  };

  const chooseAPI = async () => {
    try {
      if (!template.name || !template.language || !template.category) {
        toast.error("Please fill all required fields");
        return;
      }

      if (!selectedNumber) {
        toast.error("Please select WhatsApp number");
        return;
      }

      let response = null;

      // IMAGE
      if (template.headerType === "Image") {
        if (!headerImage) {
          toast.error("Please select image");
          return;
        }
  const imageName = headerImage.name
    .replace(/\s+/g, "_")
    .replace(/\.(png|jpg|jpeg)$/i, "");
        const base64 = await convertToBase64(headerImage);
        const uploadRes = await uploadImage(imageName, base64);

        if (uploadRes?.status !== "0") {
          toast.error(uploadRes?.msg || "Image upload failed");
          return;
        }

        const payload = buildMediaPayload(uploadRes.image_url, "image");

        response = await createImageTemplate(payload);
      }

      // VIDEO
      else if (template.headerType === "Video") {
        if (!headerVideo) {
          toast.error("Please select video");
          return;
        }

        const uploadRes = await uploadVideo(headerVideo);
        if (uploadRes?.responseCode !== "00") {
          toast.error(uploadRes?.responseMessage || "Video upload failed");
          return;
        }

        const mediaUrl = uploadRes?.data?.mediaUrl;

        const payload = buildMediaPayload(mediaUrl, "video");

        response = await createVideoTemplate(payload);
      }
      // DOCUMENT
      else if (template.headerType === "Document") {
        if (!headerDocument) {
          toast.error("Please select document");
          return;
        }

        const uploadRes = await uploadDocument(headerDocument);

        if (uploadRes?.responseCode !== "00") {
          toast.error(uploadRes?.responseMessage || "Document upload failed");
          return;
        }

        const documentUrl = uploadRes?.data?.mediaUrl;

        if (!documentUrl) {
          toast.error("Document URL missing");
          return;
        }

        const payload = buildDocumentTemplatePayload(documentUrl);

        console.log(
          "DOCUMENT BODY COMPONENT:",
          payload.components.find((item) => item.type === "BODY"),
        );

        console.log("CREATE DOCUMENT TEMPLATE PAYLOAD:", payload);

        response = await createDocumentTemplate(payload);
      }

      // TEXT
      else {
        const payload = buildCreateTemplatePayload();
        console.log("CREATE TEMPLATE PAYLOAD:", payload);

        response = await createTemplate(payload);
      }

      if (response?.responseCode === "00" && !response?.response?.error) {
        toast.success(
          response?.responseMessage || "Template Created Successfully",
        );
        await fetchVendorTemplates(selectedNumber);
      } else {
        toast.error(
          response?.response?.error?.error_user_msg ||
            response?.response?.error?.message ||
            response?.responseMessage ||
            "Template creation failed",
        );
      }
    } catch (err) {
      console.error("Template Creation Error FULL:", err);
      toast.error(err?.message || "Something went wrong");
    }
  };

  const emojiCategories = {
    "😀": [
      "😀",
      "😁",
      "😂",
      "🤣",
      "😃",
      "😄",
      "😅",
      "😆",
      "😉",
      "😊",
      "😋",
      "😎",
      "😍",
      "😘",
      "😗",
      "😙",
      "😚",
      "🙂",
      "🤗",
      "🤩",
      "🥳",
      "😏",
      "😒",
      "😞",
      "😔",
      "😟",
      "😢",
      "😭",
      "😤",
      "😠",
      "😡",
      "🤬",
      "🤯",
      "😳",
      "🥺",
      "😨",
      "😰",
      "😥",
      "🤥",
      "🤫",
      "🤭",
      "🫢",
      "🫡",
      "🤔",
      "🙄",
      "😬",
      "😴",
      "🥱",
      "🤤",
      "🤢",
    ],

    "🖐️": [
      "👋",
      "🤚",
      "🖐️",
      "✋",
      "🖖",
      "👌",
      "🤌",
      "🤏",
      "✌️",
      "🤞",
      "🤟",
      "🤘",
      "🤙",
      "👈",
      "👉",
      "👆",
      "🖕",
      "👇",
      "☝️",
      "👍",
      "👎",
      "✊",
      "👊",
      "🤛",
      "🤜",
      "👏",
      "🙌",
      "👐",
      "🤲",
      "🙏",
    ],

    "🍕": [
      "🍏",
      "🍎",
      "🍐",
      "🍊",
      "🍋",
      "🍌",
      "🍉",
      "🍇",
      "🍓",
      "🫐",
      "🥭",
      "🍍",
      "🥥",
      "🥑",
      "🥦",
      "🥒",
      "🌽",
      "🍠",
      "🥔",
      "🥕",
      "🌰",
      "🥜",
      "🍞",
      "🥐",
      "🥖",
      "🫓",
      "🥞",
      "🧇",
      "🍗",
      "🍖",
      "🍕",
      "🌭",
      "🍔",
      "🍟",
      "🍜",
      "🍣",
      "🍩",
      "🍪",
      "🎂",
      "🍫",
    ],

    "🎉": [
      "🎃",
      "🎄",
      "🎆",
      "🎇",
      "🎈",
      "🎉",
      "🎊",
      "🎁",
      "🎀",
      "🎤",
      "🎧",
      "🎼",
      "🎷",
      "🎸",
      "🎻",
      "🥁",
      "🎮",
      "🕹️",
      "🎰",
      "🎳",
      "⛳",
      "🥊",
      "🏀",
      "⚽",
      "🏈",
      "⚾",
      "🥎",
      "🎾",
      "🎱",
      "🏆",
    ],

    "🚗": [
      "🚗",
      "🚕",
      "🚙",
      "🚌",
      "🚎",
      "🏎️",
      "🚓",
      "🚑",
      "🚒",
      "🚜",
      "✈️",
      "🚀",
      "🛸",
      "⛵",
      "🛳️",
      "🗺️",
      "🏝️",
      "🏞️",
      "🏰",
      "🗽",
    ],

    "💡": [
      "❤️",
      "💙",
      "💚",
      "💛",
      "💜",
      "🖤",
      "🤍",
      "🤎",
      "💔",
      "❣️",
      "💥",
      "💢",
      "💦",
      "💨",
      "🔥",
      "⭐",
      "🌟",
      "✨",
      "⚡",
      "💫",
      "💬",
      "🗨️",
      "🗯️",
      "💭",
      "🕒",
      "⏳",
      "🕰️",
      "🛑",
      "🚸",
      "⚠️",
    ],

    "📞": [
      "📞",
      "☎️",
      "📱",
      "📲",
      "🖥️",
      "💻",
      "⌨️",
      "🖱️",
      "🖲️",
      "📟",
      "📠",
      "📺",
      "📻",
      "🎙️",
      "📡",
      "📩",
      "✉️",
      "📧",
      "📨",
      "📬",
    ],

    "💼": [
      "💼",
      "📊",
      "📈",
      "📉",
      "📅",
      "📆",
      "📝",
      "🖊️",
      "🖋️",
      "✒️",
      "📎",
      "📏",
      "📐",
      "📂",
      "📌",
      "🔖",
      "🗄️",
      "🏢",
      "🏦",
      "🏛️",
    ],
  };

  const countryCodes = [
    { code: "91", label: "🇮🇳 India (+91)" },
    { code: "1", label: "🇺🇸 USA (+1)" },
    { code: "44", label: "🇬🇧 UK (+44)" },
    { code: "61", label: "🇦🇺 Australia (+61)" },
    { code: "81", label: "🇯🇵 Japan (+81)" },
    { code: "971", label: "🇦🇪 UAE (+971)" },
    { code: "49", label: "🇩🇪 Germany (+49)" },
    { code: "33", label: "🇫🇷 France (+33)" },
    { code: "39", label: "🇮🇹 Italy (+39)" },
    { code: "34", label: "🇪🇸 Spain (+34)" },
    { code: "7", label: "🇷🇺 Russia (+7)" },
    { code: "86", label: "🇨🇳 China (+86)" },
    { code: "55", label: "🇧🇷 Brazil (+55)" },
    { code: "27", label: "🇿🇦 South Africa (+27)" },
    { code: "62", label: "🇮🇩 Indonesia (+62)" },
    { code: "82", label: "🇰🇷 South Korea (+82)" },
    { code: "90", label: "🇹🇷 Turkey (+90)" },
    { code: "52", label: "🇲🇽 Mexico (+52)" },
    { code: "880", label: "🇧🇩 Bangladesh (+880)" },
    { code: "92", label: "🇵🇰 Pakistan (+92)" },
    { code: "66", label: "🇹🇭 Thailand (+66)" },
    { code: "20", label: "🇪🇬 Egypt (+20)" },
    { code: "98", label: "🇮🇷 Iran (+98)" },
    { code: "31", label: "🇳🇱 Netherlands (+31)" },
    { code: "46", label: "🇸🇪 Sweden (+46)" },
    { code: "47", label: "🇳🇴 Norway (+47)" },
    { code: "48", label: "🇵🇱 Poland (+48)" },
    { code: "32", label: "🇧🇪 Belgium (+32)" },
    { code: "30", label: "🇬🇷 Greece (+30)" },
    { code: "43", label: "🇦🇹 Austria (+43)" },
    { code: "351", label: "🇵🇹 Portugal (+351)" },
    { code: "64", label: "🇳🇿 New Zealand (+64)" },
    { code: "63", label: "🇵🇭 Philippines (+63)" },
    { code: "41", label: "🇨🇭 Switzerland (+41)" },
    { code: "45", label: "🇩🇰 Denmark (+45)" },
    { code: "351", label: "🇵🇹 Portugal (+351)" },
    { code: "56", label: "🇨🇱 Chile (+56)" },
    { code: "93", label: "🇦🇫 Afghanistan (+93)" },
    { code: "94", label: "🇱🇰 Sri Lanka (+94)" },
    { code: "233", label: "🇬🇭 Ghana (+233)" },
    { code: "234", label: "🇳🇬 Nigeria (+234)" },
    { code: "36", label: "🇭🇺 Hungary (+36)" },
    { code: "212", label: "🇲🇦 Morocco (+212)" },
    { code: "213", label: "🇩🇿 Algeria (+213)" },
    { code: "216", label: "🇹🇳 Tunisia (+216)" },
    { code: "250", label: "🇷🇼 Rwanda (+250)" },
    { code: "254", label: "🇰🇪 Kenya (+254)" },
    { code: "256", label: "🇺🇬 Uganda (+256)" },
    { code: "62", label: "🇮🇩 Indonesia (+62)" },
  ];

  const [template, setTemplate] = useState({
    name: "",
    language: "",
    category: "",
    headerType: "",
    headerText: "",
    body: "",
    footer: "Powered By Isanchaar",
    button1: "",
    button2: "",
  });

  const addVariable = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const nextVarNum = variables.length + 1;
    const variableText = ` {{${nextVarNum}}} `;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const newBody =
      template.body.substring(0, start) +
      variableText +
      template.body.substring(end);

    setTemplate((prev) => ({
      ...prev,
      body: newBody,
    }));

    setVariables((prev) => [...prev, { label: variableText, value: "" }]);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + variableText.length,
        start + variableText.length,
      );
    }, 0);
  };

  const handleVariableValueChange = (index, value) => {
    const updated = [...variables];
    updated[index].value = value;
    setVariables(updated);
  };

  const handleButtonTypeChange = (e) => {
    const { value } = e.target;

    if (value === "4") {
      setTemplate((prev) => ({
        ...prev,
        showButton4option_quick1: "4",
      }));
    }

    if (value === "5") {
      setTemplate((prev) => ({
        ...prev,
        showButton4option_quick2: "5",
      }));
    }

    if (value === "6") {
      setTemplate((prev) => ({
        ...prev,
        showButton4option_quick3: "6",
      }));
    }

    if (value === "7") {
      setTemplate((prev) => ({
        ...prev,
        showButton4option_quick4: "7",
      }));
    }
  };

  const applyFormatting = (type) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    if (start === end) return;

    const selectedText = template.body.substring(start, end);

    let formattedText = selectedText;

    if (type === "bold") {
      formattedText = `*${selectedText}*`;
    }

    if (type === "italic") {
      formattedText = `_${selectedText}_`;
    }

    if (type === "boldItalic") {
      formattedText = `_*${selectedText}*_`;
    }

    const newText =
      template.body.substring(0, start) +
      formattedText +
      template.body.substring(end);

    setTemplate((prev) => ({
      ...prev,
      body: newText,
    }));

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + formattedText.length);
    }, 0);
  };

  const insertEmoji = (emoji) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { selectionStart } = textarea;

    const newText =
      template.body.slice(0, selectionStart) +
      emoji +
      template.body.slice(selectionStart);

    setTemplate({ ...template, body: newText });

    setShowEmojiPicker(false);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        selectionStart + emoji.length,
        selectionStart + emoji.length,
      );
    }, 0);
  };

  const handleHeaderTypeChange = (value) => {
    setTemplate({
      ...template,
      headerType: value,
    });

    // reset media
    setHeaderImage(null);
    setHeaderVideo(null);
    setHeaderDocument(null);
  };

  const handleRemoveButton4_quick1 = () => {
    setTemplate((prev) => ({
      ...prev,
      showButton4_quick1: "",
      showButton4option_quick1: "",
    }));
  };

  const handleRemoveButton4_quick2 = () => {
    setTemplate((prev) => ({
      ...prev,
      showButton4_quick2: "",
      showButton4option_quick2: "",
    }));
  };

  const handleRemoveButton4_quick3 = () => {
    setTemplate((prev) => ({
      ...prev,
      showButton4_quick3: "",
      showButton4_quick3_url: "",
      showButton4option_quick3: "",
    }));
  };

  const handleRemoveButton4_quick4 = () => {
    setTemplate((prev) => ({
      ...prev,
      showButton4_quick4: "",
      showButton4_quick3_call: "",
      showButton4option_quick4: "",
    }));
  };

  const handlePhoneChange = (e) => {
    let inputValue = e.target.value.replace(/\D/g, "");

    if (!inputValue.startsWith(selectedCode)) {
      inputValue = selectedCode + inputValue;
    }

    setTemplate((prev) => ({
      ...prev,
      showButton4_quick3_call: inputValue,
    }));
  };

  const replaceVariables = (text = "") => {
    return variables.reduce((acc, variable, index) => {
      const pattern = new RegExp(`\\{\\{${index + 1}\\}\\}`, "g");
      return acc.replace(pattern, variable.value || "");
    }, text);
  };

  const renderFormattedText = (text = "") => {
    let formatted = text;

    // Escape HTML
    formatted = formatted
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    // Bold Italic first
    formatted = formatted.replace(/_\*(.*?)\*_/g, "<b><i>$1</i></b>");
    formatted = formatted.replace(/\*_(.*?)_\*/g, "<b><i>$1</i></b>");

    // Bold
    formatted = formatted.replace(/\*(.*?)\*/g, "<b>$1</b>");

    // Italic
    formatted = formatted.replace(/_(.*?)_/g, "<i>$1</i>");

    // Line breaks
    formatted = formatted.replace(/\n/g, "<br/>");

    return formatted;
  };

  /* ================= LOAD LANGUAGES ================= */
  useEffect(() => {
    const load = async () => {
      const res = await getLanguages();
      if (res?.responseCode === "00") {
        setLanguages(res.data);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (whatsappNumbers?.length > 0) {
      setSelectedNumber(whatsappNumbers[0].phone_no_id);
    }
  }, [whatsappNumbers]);

  return (
    <>
      <NoIndex />
      <ToastContainer />
      <div className=" min-h-full p-8 montserrat-font-1">
        <div className="max-w-8xl mx-auto flex gap-10 montserrat-font-1">
          {/* ================= LEFT FORM ================= */}
          <div className="w-2/3 space-y-6 montserrat-font-1">
            <h2 className="text-2xl font-semibold">Create New Template</h2>
            {/* TOP CARD */}
            <div className="bg-white rounded-xl shadow p-6 space-y-5 border montserrat-font-1">
              {/* WhatsApp Number */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Select WhatsApp Number
                </label>
                <select
                  value={selectedNumber}
                  onChange={(e) => setSelectedNumber(e.target.value)}
                  className="w-full border rounded-lg px-4 py-2"
                >
                  {whatsappNumbers?.map((num) => (
                    <option key={num.phone_no_id} value={num.phone_no_id}>
                      {num.phone_no_id} - ({num.phone_no})
                    </option>
                  ))}
                </select>
              </div>

              {/* Template Name */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Template Name
                </label>
                <input
                  value={template.name}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (value.length > 0) {
                      value = value.charAt(0).toLowerCase() + value.slice(1);
                    }

                    setTemplate({ ...template, name: value });
                  }}
                  className="w-full border rounded-lg px-4 py-2"
                />
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Template Language
                </label>
                <select
                  value={template.language}
                  onChange={(e) =>
                    setTemplate({ ...template, language: e.target.value })
                  }
                  className="w-full border rounded-lg px-4 py-2"
                >
                  <option value="">Select Language</option>
                  {languages.map((lan, i) => (
                    <option key={i} value={lan.language_code}>
                      {lan.language}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Template Category
                </label>
                <select
                  value={template.category}
                  onChange={(e) =>
                    setTemplate({ ...template, category: e.target.value })
                  }
                  className="w-full border rounded-lg px-4 py-2"
                >
                  <option value="">Select Category</option>
                  <option value="MARKETING">Marketing</option>
                  <option value="UTILITY">Utility</option>
                </select>
              </div>
            </div>

            {/* ================= HEADER ================= */}
            <div className="bg-white rounded-xl shadow p-6">
              <div className="flex items-center justify-between">
                <span className="font-medium">Header</span>

                {/* Toggle Switch */}
                <button
                  onClick={() => setIncludeHeader(!includeHeader)}
                  className={`w-12 h-6 flex items-center rounded-full p-1 transition ${
                    includeHeader ? "bg-blue-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`bg-white w-4 h-4 rounded-full shadow-md transform transition ${
                      includeHeader ? "translate-x-6" : ""
                    }`}
                  ></div>
                </button>
              </div>

              {includeHeader && (
                <div className="mt-4 space-y-4 montserrat-font-1">
                  {/* Header Type Dropdown */}
                  <select
                    value={template.headerType}
                    onChange={(e) => handleHeaderTypeChange(e.target.value)}
                    className="w-full border rounded-lg px-4 py-2"
                  >
                    <option value="">Select Header Type</option>
                    <option value="Text">Text</option>
                    <option value="Image">Image</option>
                    <option value="Video">Video</option>
                    <option value="Document">Document</option>
                  </select>

                  {/* ================= TEXT ================= */}
                  {template.headerType === "Text" && (
                    <textarea
                      value={template.headerText}
                      onChange={(e) =>
                        setTemplate({
                          ...template,
                          headerText: e.target.value,
                        })
                      }
                      placeholder="Enter Header Text"
                      className="w-full border rounded-lg px-4 py-2"
                    />
                  )}

                  {/* ================= IMAGE ================= */}
                  {template.headerType === "Image" && (
                    <div className="space-y-3">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setHeaderImage(e.target.files[0])}
                      />

                      {headerImage && (
                        <img
                          src={URL.createObjectURL(headerImage)}
                          alt="preview"
                          className="rounded-lg max-h-40"
                        />
                      )}
                    </div>
                  )}

                  {/* ================= VIDEO ================= */}
                  {template.headerType === "Video" && (
                    <div className="space-y-3">
                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => setHeaderVideo(e.target.files[0])}
                      />

                      {headerVideo && (
                        <video
                          controls
                          src={URL.createObjectURL(headerVideo)}
                          className="rounded-lg max-h-40"
                        />
                      )}
                    </div>
                  )}

                  {/* ================= DOCUMENT ================= */}
                  {template.headerType === "Document" && (
                    <div className="space-y-3">
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => setHeaderDocument(e.target.files[0])}
                      />

                      {headerDocument && (
                        <div className="p-3 bg-gray-100 rounded-lg text-sm">
                          📄 {headerDocument.name}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ================= BODY ================= */}
            <div className="bg-white rounded-xl shadow p-6 space-y-4 relative montserrat-font-1">
              <label className="font-medium">Body</label>

              <textarea
                ref={textareaRef}
                value={template.body}
                onChange={(e) =>
                  setTemplate({ ...template, body: e.target.value })
                }
                className="w-full border rounded-lg px-4 py-3 h-28"
              />

              <div className="flex justify-between items-center montserrat-font-1">
                <div className="flex gap-3">
                  {/* Emoji Button */}
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="bg-gray-200 px-3 py-1 rounded"
                  >
                    😀
                  </button>

                  {/* Bold */}
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      applyFormatting("bold");
                    }}
                    className="bg-black text-white px-3 py-1 rounded"
                  >
                    B
                  </button>

                  {/* Italic */}
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      applyFormatting("italic");
                    }}
                    className="bg-black text-white px-3 py-1 rounded"
                  >
                    I
                  </button>

                  {/* Bold Italic */}
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      applyFormatting("boldItalic");
                    }}
                    className="bg-black text-white px-3 py-1 rounded"
                  >
                    BI
                  </button>
                </div>

                <button
                  type="button"
                  onClick={addVariable}
                  className="bg-blue-500 text-white px-4 py-1 rounded"
                >
                  Add variable
                </button>
              </div>

              {variables.map((variable, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 mt-2 montserrat-font-1"
                >
                  <span className="text-sm font-medium">{variable.label}</span>
                  <input
                    type="text"
                    value={variable.value}
                    onChange={(e) =>
                      handleVariableValueChange(index, e.target.value)
                    }
                    className="flex-1 border rounded-lg px-3 py-1"
                  />
                </div>
              ))}

              {/* ================= EMOJI PICKER ================= */}
              {showEmojiPicker && (
                <div className="absolute bg-white shadow-lg rounded-lg p-3 mt-2 montserrat-font-1 max-h-60 overflow-y-auto w-80 z-50">
                  {/* Category Tabs */}
                  <div className="flex gap-3 mb-2 border-b pb-2">
                    {Object.keys(emojiCategories).map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setSelectedEmojiCategory(cat)}
                        className={`text-lg ${
                          selectedEmojiCategory === cat ? "font-bold" : ""
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>

                  {/* Emoji List */}
                  <div className="grid grid-cols-8 gap-2 text-lg">
                    {emojiCategories[selectedEmojiCategory].map((emoji) => (
                      <button key={emoji} onClick={() => insertEmoji(emoji)}>
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ================= FOOTER ================= */}
<div className="bg-white rounded-xl shadow p-6 montserrat-font-1">
  <label className="block font-medium mb-2">Footer</label>

<input
  type="text"
  value={template.footer}
  onChange={(e) =>
    setTemplate((prev) => ({
      ...prev,
      footer: e.target.value,
    }))
  }
  className="w-full border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
/>
</div>

            {/* ================= BUTTON ================= */}
            <div className="bg-white rounded-xl shadow p-6 space-y-4 montserrat-font-1">
              {/* Toggle Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="font-medium">Buttons</span>
                  <span className="text-xs text-red-500">
                    *Maximum 3 buttons allowed
                  </span>
                </div>

                <button
                  onClick={() => setIncludeButtons(!includeButtons)}
                  className={`w-12 h-6 flex items-center rounded-full p-1 transition ${
                    includeButtons ? "bg-blue-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`bg-white w-4 h-4 rounded-full shadow-md transform transition ${
                      includeButtons ? "translate-x-6" : ""
                    }`}
                  />
                </button>
              </div>

              {includeButtons && (
                <>
                  {/* Select Button Type */}
                  <select
                    className="w-full border rounded-lg px-4 py-2"
                    onChange={(e) => handleButtonTypeChange(e)}
                  >
                    <option value="">Select Button Type</option>
                    <option value="4">Quick Reply Button 1</option>
                    <option value="5">Quick Reply Button 2</option>
                    <option value="6">Visit Website</option>
                    <option value="7">Call Phone Number</option>
                  </select>

                  {/* QUICK 1 */}
                  {template.showButton4option_quick1 === "4" && (
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        placeholder="Button Name"
                        value={template.showButton4_quick1}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            showButton4_quick1: e.target.value,
                          })
                        }
                        className="flex-1 border rounded-lg px-4 py-2"
                      />

                      <button
                        onClick={handleRemoveButton4_quick1}
                        className="bg-red-500 text-white px-3 py-2 rounded-lg"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  {/* QUICK 2 */}
                  {template.showButton4option_quick2 === "5" && (
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        placeholder="Button Name"
                        value={template.showButton4_quick2}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            showButton4_quick2: e.target.value,
                          })
                        }
                        className="flex-1 border rounded-lg px-4 py-2"
                      />

                      <button
                        onClick={handleRemoveButton4_quick2}
                        className="bg-red-500 text-white px-3 py-2 rounded-lg"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  {/* URL BUTTON */}
                  {template.showButton4option_quick3 === "6" && (
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        placeholder="Button Name"
                        value={template.showButton4_quick3}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            showButton4_quick3: e.target.value,
                          })
                        }
                        className="flex-1 border rounded-lg px-4 py-2"
                      />

                      <input
                        type="text"
                        placeholder="Website URL"
                        value={template.showButton4_quick3_url}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            showButton4_quick3_url: e.target.value,
                          })
                        }
                        className="flex-1 border rounded-lg px-4 py-2"
                      />

                      <button
                        onClick={handleRemoveButton4_quick3}
                        className="bg-red-500 text-white px-3 py-2 rounded-lg"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  {/* CALL BUTTON */}
                  {template.showButton4option_quick4 === "7" && (
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        placeholder="Button Name"
                        value={template.showButton4_quick4}
                        onChange={(e) =>
                          setTemplate({
                            ...template,
                            showButton4_quick4: e.target.value,
                          })
                        }
                        className="flex-1 border rounded-lg px-4 py-2"
                      />

                      <select
                        value={selectedCode}
                        onChange={(e) => setSelectedCode(e.target.value)}
                        className="border rounded-lg px-3 py-2"
                      >
                        {countryCodes.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.label}
                          </option>
                        ))}
                      </select>

                      <input
                        type="text"
                        placeholder="Phone Number"
                        value={template.showButton4_quick3_call}
                        onChange={handlePhoneChange}
                        className="flex-1 border rounded-lg px-4 py-2"
                      />

                      <button
                        onClick={handleRemoveButton4_quick4}
                        className="bg-red-500 text-white px-3 py-2 rounded-lg"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-start montserrat-font-1">
              <button
                type="button"
                onClick={chooseAPI}
                className="px-6 py-2 mt-4 bg-blue-600 text-white rounded-lg 
               hover:bg-blue-700 active:scale-95 
               transition-all duration-200 shadow-md"
              >
                Submit
              </button>
            </div>
          </div>

          {/* ================= RIGHT MOBILE PREVIEW ================= */}
          <div className="w-1/3 flex justify-center items-center montserrat-font-1">
            <div>
              <h3 className="text-center font-medium mb-4 text-green-800">
                Sample Message Preview
              </h3>

              <div className="mobile-card montserrat-font-1">
                <div className="mobile-top"></div>
                <div className="mobile-speaker"></div>
                <div className="mobile-camera"></div>
                <div className="mobile-btn1"></div>
                <div className="mobile-btn2"></div>
                <div className="mobile-btn3"></div>

                <div className="mobile-card-int">
                  <div className="bubble-container">
                    {/* MESSAGE BUBBLE */}
                    <div className="message-bubble message-bubble-animate">
                      {/* HEADER */}
                      {includeHeader &&
                        template.headerType === "Text" &&
                        template.headerText && (
                          <div className="header-text">
                            {template.headerText}
                          </div>
                        )}

                      {includeHeader &&
                        template.headerType === "Image" &&
                        headerImage && (
                          <img
                            src={URL.createObjectURL(headerImage)}
                            alt="preview"
                            style={{ width: "100%", borderRadius: "5px" }}
                          />
                        )}

                      {includeHeader &&
                        template.headerType === "Video" &&
                        headerVideo && (
                          <video
                            controls
                            src={URL.createObjectURL(headerVideo)}
                            style={{ width: "100%", borderRadius: "5px" }}
                          />
                        )}

                      {includeHeader &&
                        template.headerType === "Document" &&
                        headerDocument && (
                          <div className="w-full rounded-lg overflow-hidden bg-[#f1f3f4] mb-2 border border-gray-200">
                            <div className="h-28 bg-[#fafafa] flex items-center justify-center">
                              <div className="relative">
                                <div className="w-16 h-20 bg-white rounded-md border border-gray-300 shadow-sm flex flex-col items-center justify-center">
                                  <span className="text-2xl">📄</span>
                                  <span className="text-[10px] font-bold text-gray-700 mt-1">
                                    {getDocumentExtension(headerDocument.name)}
                                  </span>
                                </div>

                                <div className="absolute -bottom-2 -right-2 bg-green-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs shadow">
                                  ✓
                                </div>
                              </div>
                            </div>

                            <div className="bg-white px-3 py-2 border-t border-gray-200">
                              <p className="text-[12px] font-semibold text-gray-900 truncate m-0">
                                {headerDocument.name}
                              </p>

                              <div className="flex items-center justify-between mt-1">
                                <p className="text-[10px] text-gray-500 m-0">
                                  {getDocumentExtension(headerDocument.name)}{" "}
                                  Document
                                </p>

                                <p className="text-[10px] text-gray-500 m-0">
                                  {formatFileSize(headerDocument.size)}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                      {/* BODY */}
                      {template.body && (
                        <div
                          className="body-text"
                          dangerouslySetInnerHTML={{
                            __html: renderFormattedText(
                              replaceVariables(template.body),
                            ),
                          }}
                        />
                      )}

                      {/* FOOTER */}
                      <div className="footer-text">{template.footer}</div>

                      <div className="message-time">
                        {new Date().toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </div>
                    </div>

                    {/* BUTTONS */}
                    {includeButtons && (
                      <div className="button-bubble button-bubble-animate">
                        {template.showButton4_quick1 && (
                          <div className="button-text">
                            {template.showButton4_quick1}
                          </div>
                        )}

                        {template.showButton4_quick2 && (
                          <div className="button-text">
                            {template.showButton4_quick2}
                          </div>
                        )}

                        {template.showButton4_quick3 && (
                          <div className="button-text">
                            {template.showButton4_quick3}
                          </div>
                        )}

                        {template.showButton4_quick4 && (
                          <div className="button-text">
                            {template.showButton4_quick4}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateTemplate;
